import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { Button } from "../../components/ui/button";

export default function QuestionnaireList() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);

  useEffect(() => {
    api.get(`/cases/${caseId}`).then((r) => setBundle(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [caseId]);

  if (!bundle) return <DashboardShell title="Questionnaires"><div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></DashboardShell>;

  const done = (bundle.responses || []).map((r) => r.measure);
  const assigned = bundle.measures_assigned || [];
  const allDone = assigned.length > 0 && assigned.every((m) => done.includes(m));

  return (
    <DashboardShell title="Your questionnaires" subtitle="Validated, widely used measures. Answer honestly — there are no right or wrong answers. Your clinician reviews every response.">
      <div className="max-w-3xl space-y-4">
        {assigned.map((m) => {
          const resp = (bundle.responses || []).find((r) => r.measure === m);
          return (
            <div key={m} className="card-warm p-6 flex items-center justify-between gap-4" data-testid={`questionnaire-row-${m}`}>
              <div>
                <p className="font-heading text-xl font-semibold">{resp?.measure_name || m.toUpperCase()}</p>
                {resp ? (
                  <p className="text-sm text-[#5C5C57] mt-1" data-testid={`questionnaire-score-${m}`}>
                    Completed · Your clinician will interpret this measure within the full evaluation
                  </p>
                ) : (
                  <p className="text-sm text-[#5C5C57] mt-1">Takes about 3–5 minutes</p>
                )}
              </div>
              {resp ? (
                <span className="flex items-center gap-2 text-[#2C5A2E] text-sm font-semibold" data-testid={`questionnaire-done-${m}`}>
                  <CheckCircle2 className="h-5 w-5" /> Done
                </span>
              ) : (
                <Button data-testid={`questionnaire-start-${m}`} onClick={() => navigate(`/app/case/${caseId}/questionnaire/${m}`)}
                  className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
                  Begin <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          );
        })}
        {allDone && (
          <div className="card-warm p-8 text-center" data-testid="questionnaires-complete">
            <p className="font-heading text-2xl font-semibold">All questionnaires complete</p>
            <p className="text-sm text-[#5C5C57] mt-2">The final step before your report: book your clinician interview.</p>
            <Button data-testid="goto-booking-btn" onClick={() => navigate(`/app/case/${caseId}/book`)}
              className="mt-6 rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white">
              Book your interview <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
